/**
 * A tab that outlives a deploy: deploys keep the previous builds' files, but should a chunk this
 * tab's build names be gone all the same, only a load the shopper asked for (a page, an overlay)
 * reloads onto the current build, and only once the server is seen serving a different one, so a
 * failed background fetch or a dropped connection never reloads the page under the shopper.
 */

const RELOADED_AT = "loot:reloaded-for-build";
/** A reload within this long of the last one is refused, so a broken deploy cannot loop. */
const RELOAD_GUARD_MS = 30_000;

/** A dynamic import or its preloaded stylesheet failed to load, in any browser. */
export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : "";
  return /^(Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS)/.test(
    message,
  );
}

/** The entry script a document loads, as an absolute URL. */
function entryOf(doc: Document): string | undefined {
  const src = doc
    .querySelector<HTMLScriptElement>('script[type="module"][src]')
    ?.getAttribute("src");
  return src ? new URL(src, window.location.href).href : undefined;
}

/** Whether the server's `index.html` now loads a different entry than this tab did. */
async function isBuildReplaced(): Promise<boolean> {
  const current = entryOf(document);
  if (!current) return false;
  try {
    const response = await fetch(new URL("/", window.location.href), { cache: "no-store" });
    if (!response.ok) return false;
    const served = entryOf(new DOMParser().parseFromString(await response.text(), "text/html"));
    return served !== undefined && served !== current;
  } catch {
    return false;
  }
}

/** True when a reload for a replaced build ran within the guard, or storage is unusable. */
function reloadedRecently(): boolean {
  try {
    const at = Number(sessionStorage.getItem(RELOADED_AT));
    if (at && Date.now() - at < RELOAD_GUARD_MS) return true;
    sessionStorage.setItem(RELOADED_AT, String(Date.now()));
    return false;
  } catch {
    return true;
  }
}

/**
 * After a chunk the shopper asked for failed to load: reloads onto the current build when a deploy
 * has replaced this tab's, and resolves true when it does. Any other failure resolves false and is
 * the caller's to show.
 */
export async function reloadIfBuildReplaced(error: unknown): Promise<boolean> {
  if (!isChunkLoadError(error) || !(await isBuildReplaced()) || reloadedRecently()) return false;
  window.location.reload();
  return true;
}
